import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "./adminApi";
import { Loader2, Eye, EyeOff } from "lucide-react";

const SECTION_META = {
  hero: { label: "Hero / Home", desc: "Landing headline with backdrop video" },
  stats: { label: "Stats bar", desc: "Impact metrics ribbon below hero" },
  team: { label: "Research Domains", desc: "The six interdisciplinary frontiers" },
  products: { label: "AI Products", desc: "Kainos AI + specialized agents grid" },
  initiatives: { label: "Active Initiatives", desc: "Current research project cards" },
  demo: { label: "Live Demo", desc: "AI emotion analysis playground" },
  research: { label: "Interactive Visualization", desc: "3D emotion cube" },
  publications: { label: "Publications", desc: "Peer-reviewed paper archive" },
  press: { label: "Press", desc: "News ticker" },
  events: { label: "Events", desc: "Symposia, dialogues, open days" },
  partners: { label: "Partners", desc: "Institutional logos" },
  contact: { label: "Contact / Footer", desc: "Address, contact form, colophon" },
};

const VisibilityPanel = () => {
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // section key being toggled
  const [error, setError] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/content/section-visibility");
      setSections(res.data?.sections || {});
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load section visibility.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const toggle = async (key) => {
    setPending(key);
    setError("");
    const nextValue = !sections[key];
    // optimistic
    setSections((s) => ({ ...s, [key]: nextValue }));
    try {
      await adminApi.put("/admin/content/section-visibility", {
        section_id: key,
        visible: nextValue,
      });
    } catch (err) {
      setSections((s) => ({ ...s, [key]: !nextValue }));
      setError(err?.response?.data?.detail || "Failed to update.");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-visibility-panel">
      <div>
        <h2 className="text-2xl font-light text-gray-900 tracking-tight">
          Section visibility
        </h2>
        <p className="text-sm text-gray-500 font-light mt-1">
          Show or hide each section of the public website. Changes take effect immediately.
        </p>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {Object.keys(SECTION_META).map((key, i) => {
            const meta = SECTION_META[key];
            const visible = sections[key] !== false;
            return (
              <div
                key={key}
                className={`px-5 py-4 flex items-center gap-4 ${
                  i > 0 ? "border-t border-gray-100" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{meta.label}</span>
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">
                      {key}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 font-light mt-0.5">{meta.desc}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span
                    className={`text-[10px] font-mono uppercase tracking-[0.2em] ${
                      visible ? "text-emerald-700" : "text-gray-400"
                    }`}
                  >
                    {visible ? "Visible" : "Hidden"}
                  </span>
                  <button
                    onClick={() => toggle(key)}
                    disabled={pending === key}
                    className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors ${
                      visible ? "bg-emerald-600" : "bg-gray-300"
                    } disabled:opacity-60`}
                    data-testid={`toggle-${key}`}
                  >
                    <span
                      className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition-transform ${
                        visible ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                  {visible ? (
                    <Eye className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VisibilityPanel;
